import translate from "@insite/client-framework/Translate";
import ConfirmationModal from "@thkStore/Components/ConfirmationModal";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface NavigationGuardModalProps {
    /** Whether the guard is active (e.g. clipboard has items) */
    active: boolean;
    /** Message displayed in the confirmation modal */
    message: string;
}

const NavigationGuardModal: React.FC<NavigationGuardModalProps> = ({ active, message }) => {
    const [isOpen, setIsOpen] = useState(false);
    const pendingUrl = useRef<string | null>(null);
    const beforeUnloadRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

    const handleCancel = useCallback(() => {
        pendingUrl.current = null;
        setIsOpen(false);
    }, []);

    const handleConfirm = useCallback(() => {
        // Remove beforeunload listener before navigating to prevent the browser
        // from showing a native alert after the user already confirmed via modal.
        if (beforeUnloadRef.current) {
            window.removeEventListener("beforeunload", beforeUnloadRef.current);
            beforeUnloadRef.current = null;
        }

        setIsOpen(false);
        if (pendingUrl.current === "@@BACK@@") {
            pendingUrl.current = null;
            window.history.go(-2);
        } else if (pendingUrl.current) {
            const url = pendingUrl.current;
            pendingUrl.current = null;
            window.location.href = url;
        }
    }, []);

    useEffect(() => {
        if (!active) {
            return;
        }

        // 1. Browser close/refresh
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = message;
        };
        beforeUnloadRef.current = handleBeforeUnload;
        window.addEventListener("beforeunload", handleBeforeUnload);

        // 2. Intercept clicks on <a> tags for SPA navigation
        const handleClick = (e: MouseEvent) => {
            const anchor = (e.target as HTMLElement).closest("a");
            if (!anchor) {
                return;
            }

            const href = anchor.getAttribute("href");
            if (!href || href.startsWith("#") || /^\s*javascript:/i.test(href)) {
                return;
            }

            // Ignore if modifier keys are held (new tab, etc.)
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
                return;
            }

            // Ignore target="_blank" links
            if (anchor.target === "_blank") {
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            pendingUrl.current = anchor.href; // Use resolved absolute URL
            setIsOpen(true);
        };
        document.addEventListener("click", handleClick, true);

        // 3. Handle browser back/forward buttons
        const currentUrl = window.location.href;
        const handlePopState = () => {
            window.history.pushState(null, "", currentUrl);
            pendingUrl.current = "@@BACK@@";
            setIsOpen(true);
        };
        window.history.pushState(null, "", currentUrl);
        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            beforeUnloadRef.current = null;
            document.removeEventListener("click", handleClick, true);
            window.removeEventListener("popstate", handlePopState);
        };
    }, [active, message]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            headline={translate("Leave this page?")}
            message={message}
            confirmText={translate("Leave")}
            cancelText={translate("Stay")}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
        />
    );
};

export default NavigationGuardModal;
